import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  HostListener
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { DomSanitizer, SafeUrl, Title } from "@angular/platform-browser";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";

import { Color } from "@angular-material-components/color-picker";

import { TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash";

import { BehaviorSubject, Observable, of, Subject, Subscription, zip } from "rxjs";
import { filter, map, skip, takeUntil } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppMessage } from "src/app/models/common/appMessage";
import {
  createProductGroup,
  getCategoriesByUsage,
  getProductGroup,
  getPropertiesByUsage,
  resetProductGroupState,
  updateProductGroup,
} from "src/app/store/actions/productGroups/productGroup.actions";
import {
  AppState,
  createdProductGroupState,
  loadedProductGroupIsLoading,
  loadedProductGroupState,
  loadingProductGroupCategoriesState,
  loadingProductGroupPropertiesState,
  loginStateLocaleList,
  messagesStateGetMessage,
} from "src/app/store/reducers";

import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { ProductGroupExtendedDTO } from "src/app/models/Products/productGroupDTO";
import { TopCard } from "src/app/models/top-card/top-card.model";
import { FormUtils } from "src/app/shared/utils/formUtils";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { CategoriesAndPropsUtils } from "src/app/shared/utils/categoriesAndPropsUtils";
import { CanComponentDeactivate } from "src/app/shared/guard/pending.guard";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { MatDialog } from "@angular/material/dialog";
import { UserLocaleDTO } from "src/app/models/UserDetails/userLocaleDTO";
import { buildDetailedDescription, populateDetailedDescription, setDetailedDescription, setFormAfterDialogClosed } from "src/app/shared/utils/localeDescriptionUtils";
import { DescriptionLocaleFormDialogComponent } from "src/app/shared/modules/dialogs/description-locale-form-dialog/description-locale-form-dialog.component";
import { ManageFilesDialogComponent } from "src/app/shared/modules/dialogs/manage-files-dialog/manage-files-dialog.component";
import { ManageFileService } from "src/app/shared/services/manage-files/manage-files.service";

export const PRODUCT_GROUP_PARAMS = {
  code: "code",
  description: "description",
  detailedDescription: "detailedDescription",
  enabled: "enabled",
  color: "color",
  imageUrl: "imageUrl",
  categories: "categories",
  properties: "properties",
};
const USAGE_CODE = "PRODUCT_GROUP";
@Component({
  selector: "app-product-group-details",
  templateUrl: "./product-group-details.component.html",
  styleUrls: ["./product-group-details.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ProductGroupDetailsComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  //Product Group
  isLoading$: Observable<boolean>;
  loadedProductGroup$: Observable<ProductGroupExtendedDTO>;
  loadedProductGroupSubs: Subscription;
  multiSubs: Subscription;

  //Files
  selectedFiles: string[];
  public selectedFilesChip: { name: string; id: string }[] = [];
  
  //Thumbnail
  public thumbnail$ = new BehaviorSubject<SafeUrl | null>(null);
  private thumbnailCache: Record<string, SafeUrl> = {};
  private fileThumbnails: Record<string, Observable<SafeUrl>> = {};
  private destroy$ = new Subject<void>();

  //Category List
  categoryList$: Observable<CategoryDTO[]>;
  categoryList: CategoryDTO[];

  //Property List
  propertyList$: Observable<PropertyDTO[]>;
  propertyList: PropertyDTO[];

  created$: Observable<boolean>;
  createdSub: Subscription;

  //Locales
  localesList$: Observable<UserLocaleDTO[]>;
  localesListSub: Subscription;

  //Top-card configuration
  card: TopCard;
  icon: any = "add";

  //Messages
  message$: Observable<AppMessage>;
  messageSubs: Subscription;

  //Navigation
  currToken: string;
  currentProductGroup: ProductGroupExtendedDTO;
  current: any;

  //Actions
  creation: boolean = false;
  duplication: boolean = false;

  //Form
  productGroupDataForm: FormGroup;

  //Language
  langSubscription: Subscription;

  // loaded locale
  public locale: string = localStorage.getItem("locale");
  localeDesc: string;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private manageFileService: ManageFileService,
    private sanitizer: DomSanitizer,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private dialog: MatDialog
  ) {
    this.titleService.setTitle(this.translate.instant("ProductGroup.Details"));
    this.isLoading$ = this.store.pipe(select(loadedProductGroupIsLoading));
    this.message$ = this.store.pipe(select(messagesStateGetMessage));
    this.loadedProductGroup$ = this.store.pipe(select(loadedProductGroupState));
    this.categoryList$ = this.store.pipe(
      select(loadingProductGroupCategoriesState)
    );
    this.propertyList$ = this.store.pipe(
      select(loadingProductGroupPropertiesState)
    );
    this.localesList$ = this.store.pipe(select(loginStateLocaleList));
    this.created$ = this.store.pipe(select(createdProductGroupState));

    this.card = {
      avatarUrl: "",
      content: "",
      imageUrl: "",
      current: "",
      title: "ProductGroup.newProductGroup",
      subtitle: this.duplication
        ? "ProductGroup.duplication"
        : "ProductGroup.creation",
    };
  }

  confirmDialog(dialog: any, refresh = false): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    if (!refresh) {
      return dialogRef.afterClosed();
    }
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.reload();
      }
    });
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";

    this.route.params.subscribe((params) => {
      const code = params.id;
      this.creation = false;
      this.duplication = false;
      const duplicateCode = localStorage.getItem("duplicate");

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      if (code && code.length) {
        this.initialiseState(code, false);
      } else if (duplicateCode !== null) {
        this.duplication = true;
        localStorage.removeItem("duplicate");
        this.initialiseState(duplicateCode, true);
      } else {
        this.creation = true;
        this.initialiseState("", false);
      }
    });

    this.createProductGroupForm();
    this.loadProductGroupInfo();
    this.loadLangChanges();
    this.resetCreation();
    
    this.loadLocales();
  }

  resetCreation() {
    this.createdSub = this.created$.pipe(skip(1)).subscribe((created) => {
      if (created) {
        this.creation = false;
        this.duplication = false;
      }
    });
  }

  loadLocales() {
    this.localesListSub = this.localesList$.subscribe((locales) => {
      if (locales) {
        this.localeDesc = locales.find(({ code }) => code === this.locale).description;
        buildDetailedDescription(
          locales, 
          this.fb,
          this.productGroupDataForm,
          PRODUCT_GROUP_PARAMS.detailedDescription,
        );
      }
    });
  }

  createProductGroupForm() {
    this.productGroupDataForm = this.fb.group({
      [PRODUCT_GROUP_PARAMS.code]: [null, [Validators.required]],
      [PRODUCT_GROUP_PARAMS.description]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          Validators.maxLength(100),
        ],
      ],
      [PRODUCT_GROUP_PARAMS.detailedDescription]: this.fb.array([]),
      [PRODUCT_GROUP_PARAMS.enabled]: [true],
      [PRODUCT_GROUP_PARAMS.color]: [null],
      [PRODUCT_GROUP_PARAMS.imageUrl]: [null, Validators.maxLength(300)],
      [PRODUCT_GROUP_PARAMS.categories]: this.fb.array([]),
      [PRODUCT_GROUP_PARAMS.properties]: this.fb.array([]),
    });
  }

  loadProductGroupInfo() {
    this.multiSubs = zip(
      this.categoryList$.pipe(skip(1)),
      this.propertyList$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      this.loadedProductGroupSubs = this.loadedProductGroup$.subscribe(
        (productGroup: ProductGroupExtendedDTO) => {
          if (
            productGroup &&
            productGroup !== null &&
            productGroup !== undefined
          ) {
            this.currentProductGroup = cloneDeep(productGroup);

            if (
              (!this.currToken ||
                (this.currToken && this.currToken?.length === 0)) &&
              !this.duplication
            ) {
              this.currToken = this.currentProductGroup.token;
            }

            this.creation = false;
            this.card = {
              avatarUrl: "",
              content: "",
              imageUrl: "",
              current: !this.duplication ? productGroup?.token : "",
              title: !this.duplication
                ? "[" +
                  productGroup?.code +
                  "] - " +
                  (productGroup?.code?.length > 0
                    ? productGroup?.detailedDescription[this.locale]
                    : "")
                : "ProductGroup.newProductGroup",
              subtitle: !this.duplication
                ? "ProductGroup.productGroup"
                : "ProductGroup.duplication",
            };

            this.productGroupDataForm.controls[
              PRODUCT_GROUP_PARAMS.code
            ].setValue(productGroup?.code);
            this.productGroupDataForm.controls[
              PRODUCT_GROUP_PARAMS.description
            ].setValue(
              productGroup?.detailedDescription[this.locale] ? productGroup?.detailedDescription[this.locale] : ""
            );
            if (!this.duplication) {
              populateDetailedDescription(
                this.productGroupDataForm.get("detailedDescription") as FormArray,
                this.currentProductGroup.detailedDescription,
              );
            }
            this.productGroupDataForm.controls[
              PRODUCT_GROUP_PARAMS.imageUrl
            ].setValue(
              productGroup && productGroup?.imageUrl && !this.duplication
                ? productGroup?.imageUrl
                : ""
            );

            this.productGroupDataForm.controls[
              PRODUCT_GROUP_PARAMS.enabled
            ].setValue(productGroup?.enabled ? productGroup?.enabled : "");
            let color = FormUtils.hexToRgb(productGroup?.color);
            this.productGroupDataForm.controls[
              PRODUCT_GROUP_PARAMS.color
            ].setValue(
              color && productGroup?.color && !this.duplication
                ? new Color(color.r, color.g, color.b)
                : null
            );
          }
          if (productGroup || this.creation || this.duplication) {
            if (categories && categories.length) {
              this.categoryList = categories;
            }
            if (properties && properties.length) {
              this.propertyList = properties;
            }
          }

          if (productGroup.imageUrl) {
            this.loadImageFileName(productGroup.imageUrl);
          }
        }
      );
    });
    this.store.dispatch(
      getCategoriesByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );

    setTimeout(() => {
      const imageId = this.productGroupDataForm.get(
        PRODUCT_GROUP_PARAMS.imageUrl
      )?.value;
      if (imageId) {
        this.getThumbnail(imageId);
      }
      this.productGroupDataForm
        .get(PRODUCT_GROUP_PARAMS.imageUrl)
        ?.valueChanges.pipe(filter(Boolean), takeUntil(this.destroy$))
        .subscribe((imageId: string) => {
          this.loadThumbnail(imageId);
        });
    });
  }

  private loadImageFileName(token: string): void {
        this.manageFileService.fileDetails(token).subscribe({
          next: (file) => {
            this.selectedFilesChip = [{ name: file.name, id: file.id }];
          },
          error: () => {
            this.selectedFilesChip = [{ name: token, id: token }];
          }
        });
      }
    
      private loadThumbnail(fileId: string): void {
        this.getThumbnail(fileId).subscribe((thumb) => this.thumbnail$.next(thumb));
      }
    
      private getThumbnail(fileId: string): Observable<SafeUrl> {
        if (this.thumbnailCache[fileId]) {
          return of(this.thumbnailCache[fileId]);
        }
    
        if (!this.fileThumbnails[fileId]) {
          this.fileThumbnails[fileId] = this.manageFileService.getFileThumbnail(fileId).pipe(
            map((blob) => {
              const url = URL.createObjectURL(blob);
              const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
              this.thumbnailCache[fileId] = safeUrl;
              return safeUrl;
            })
          );
        }
    
        return this.fileThumbnails[fileId];
      }
    
      public openManageFilesDialog(): void {
        const currentImageUrl = this.productGroupDataForm.get(PRODUCT_GROUP_PARAMS.imageUrl)?.value;
    
        this.dialog.open(ManageFilesDialogComponent, {
          data: {
            treatedDtoSelection: currentImageUrl ? [currentImageUrl] : [],
            allowMultiple: false,
            fileType: 'image'
          },
          width: '85%',
          maxHeight: '100vh',
          panelClass: 'search-site-dialog',
          autoFocus: false,
        }).afterClosed().subscribe((selectedFiles: any[]) => {
          if (selectedFiles && selectedFiles.length > 0) {
            const selectedFile = selectedFiles[0];
    
            const imageUrl = selectedFile?.id ?? selectedFile;
    
            this.productGroupDataForm.get(PRODUCT_GROUP_PARAMS.imageUrl)?.setValue(imageUrl);
            this.productGroupDataForm.get(PRODUCT_GROUP_PARAMS.imageUrl)?.markAsDirty();
            this.productGroupDataForm.get(PRODUCT_GROUP_PARAMS.imageUrl)?.markAsTouched();
    
            this.selectedFilesChip = [
              typeof selectedFile === 'object'
                ? selectedFile
                : { name: selectedFile, id: selectedFile }
            ];
          }
        });
      }

  getColorPickerBgColor(color: Color): string {
    if (color) {
      return getColorByBgColor(color.hex);
    }
  }

  openDescriptionLocaleFormDialog() {
    const detailedDescriptionBackup = [...this.productGroupDataForm.get("detailedDescription").value];
    const dialogData = {
      data: {
        parentForm: this.productGroupDataForm,
        formParam: PRODUCT_GROUP_PARAMS.detailedDescription,
      },
      width: '400px',
    };
    const dialogRef = this.dialog.open(DescriptionLocaleFormDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        setFormAfterDialogClosed(
          this.locale, 
          this.productGroupDataForm,
          PRODUCT_GROUP_PARAMS.detailedDescription,
          PRODUCT_GROUP_PARAMS.description
        );
      } else {
        this.productGroupDataForm.get("detailedDescription").setValue(detailedDescriptionBackup);
      }
    });
  }

  onSubmit() {
    if (!this.productGroupDataForm.valid) {
      this.productGroupDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("ProductGroup.invalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (
      this.productGroupDataForm.pristine &&
      !this.creation &&
      !this.duplication
    ) {
      this.snackbarService.show({
        message: this.translate.instant("ProductGroup.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let productGroup: ProductGroupExtendedDTO = {
      code: this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.code].value ?? null,
      description: this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.description].value ?? null,
      detailedDescription: setDetailedDescription(
        this.productGroupDataForm,
        PRODUCT_GROUP_PARAMS.detailedDescription,
        PRODUCT_GROUP_PARAMS.description,
        this.locale
      ),
      color: this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.color].value
        ? this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.color].value?.hex
        : null,
      imageUrl: this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.imageUrl].value
        ? this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.imageUrl].value
        : null,
      enabled:
        this.productGroupDataForm.controls[PRODUCT_GROUP_PARAMS.enabled]
          .value ?? false,
      categories: CategoriesAndPropsUtils.setFormCategories(
        this.productGroupDataForm.controls.categories.value
      ),
      properties: CategoriesAndPropsUtils.setFormProperties(
        this.productGroupDataForm.controls.properties.value
      ),
    };

    if (this.currToken.length === 0) {
      this.store.dispatch(createProductGroup({ productGroup: productGroup }));
    } else {
      productGroup = { ...productGroup, token: this.currToken };
      this.store.dispatch(updateProductGroup({ productGroup: productGroup }));
    }
    this.productGroupDataForm.markAsUntouched();
    this.productGroupDataForm.markAsPristine();
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(
        this.translate.instant("ProductGroup.Details")
      );
    });
  }

  initialiseState(code: string, duplicate?: boolean) {
    if (code) {
      this.loadProductGroup(code);
      if (duplicate) {
        this.currToken = "";
      } else {
        this.currToken = code;
      }
    } else {
      this.reset();

      this.currToken = "";
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: "",
        title: "ProductGroup.newProductGroup",
        subtitle: this.duplication
          ? "ProductGroup.duplication"
          : "ProductGroup.creation",
      };
    }
  }

  loadProductGroup(code: string) {
    if (code && code?.length > 0) {
      this.store.dispatch(getProductGroup({ code: code }));
    }
  }

  reset() {
    this.store.dispatch(resetProductGroupState());
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  refresh() {
    if (isAuthenticated(this.router.url) && !this.productGroupDataForm.pristine) {
      this.confirmDialog(this.dialog, true);
      return;
    } else {
      this.reload();
    }
  }
  
  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["groups/group", this.currToken]);
  }

  new() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["/groups/group"]);
    this.creation = true;
  }

  duplicate() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    localStorage.setItem("duplicate", this.currToken);
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["groups/group", {}]);
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (
      !isAuthenticated(this.router.url) ||
      this.productGroupDataForm.pristine
    ) {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy(): void {
    if (this.createdSub) {
      this.createdSub.unsubscribe();
    }
    if (this.messageSubs) {
      this.messageSubs.unsubscribe();
    }
    if (this.loadedProductGroupSubs) {
      this.loadedProductGroupSubs.unsubscribe();
    }
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
